import { ProductDoc } from '@gooduncles/gu-app-schema';
import { FC, useCallback, useEffect, useState } from 'react';
import Loading from 'src/layout/Loading';
import { formatNumber } from 'src/utils/common';

import { selectUnhiddenProducts, selectUserId } from 'src/redux/slices/auth';
import { useAppSelector } from 'src/redux/store';

import useImmutableProductById from 'src/hooks/useImmutableProductById';
import { useTitle } from 'src/hooks/useTitle';

import EmptyList from 'src/components/atoms/EmptyList/EmptyList';
import TopNavigationBar from 'src/components/atoms/TopNavigationBar/TopNavigationBar';
import FavoriteProduct from 'src/components/molecules/FavoriteProduct/FavoriteProduct';
import ProductThumbnail from 'src/components/molecules/ProductThumbnail/ProductThumbnail';
import ProductVolumeController from 'src/components/organisms/ProductItem/ProductVolumeController/ProductVolumeController';
import TagProductList from 'src/components/organisms/TagProductList/TagProductList';

import classes from './ProductDetail.module.scss';

type ProductDetailProps = {
  productId: ProductDoc['_id'];
};

const ProductDetail: FC<ProductDetailProps> = ({ productId }) => {
  const userId = useAppSelector(selectUserId);
  const { product, productLoading } = useImmutableProductById(productId);
  useTitle(`GU | ${product?.fullName}`);
  const unhiddenProducts = useAppSelector(selectUnhiddenProducts);
  const [selectedTag, setSelectedTag] = useState<string | null>(product?.mainTag ?? product?.tags?.[0] ?? null);
  const unitPrice = product?.unitPrice
    ? `${product.unitPrice.amount}${product.unitPrice.unit}당 ${formatNumber(product.unitPrice.price)}원`
    : null;
  const mainTag = product?.mainTag ?? null;

  const onSelectTag = useCallback(
    (tag: string) => {
      setSelectedTag(tag);
    },
    [setSelectedTag]
  );

  useEffect(() => {
    if (selectedTag === null) {
      setSelectedTag(product?.mainTag ?? product?.tags?.[0] ?? null);
    }
  }, [product, selectedTag]);

  return (
    <>
      {productLoading ? (
        <Loading />
      ) : product ? (
        <div>
          <TopNavigationBar leftButton rightButton={<FavoriteProduct product={product} size={48} />} />
          <ProductThumbnail product={product} border={false} />

          <div className={classes.productInfo}>
            <h1 className={classes.productName}>{product.fullName}</h1>
            <div className={classes.productContent}>
              <div className={classes.productPrice}>
                <span className={classes.price}>{formatNumber(product.price)}원</span>
                {unitPrice && <span className={classes.unitPrice}>{unitPrice}</span>}
              </div>
              {userId && <ProductVolumeController product={product} />}
            </div>
          </div>

          {(!!mainTag || product.tags) && (
            <div className={classes.tagBox}>
              <h3>연관 태그</h3>
              <div className={classes.tags}>
                {mainTag && (
                  <span
                    className={`${classes.tag} ${selectedTag === mainTag ? classes.selectedTag : ''}`}
                    onClick={() => onSelectTag(mainTag)}>
                    <b>#</b>
                    {mainTag}
                  </span>
                )}
                {product.tags &&
                  product.tags.map((tag, idx) => (
                    <span
                      className={`${classes.tag} ${selectedTag === tag ? classes.selectedTag : ''}`}
                      key={tag + idx}
                      onClick={() => onSelectTag(tag)}>
                      <b>#</b>
                      {tag}
                    </span>
                  ))}
              </div>
            </div>
          )}
          {selectedTag && (
            <TagProductList tag={selectedTag} unhiddenProducts={unhiddenProducts} curProductId={productId} />
          )}
        </div>
      ) : (
        <EmptyList message='상품을 찾을 수 없습니다.' />
      )}
    </>
  );
};

export default ProductDetail;
